import { TruckIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  confirmDispensaryOrder,
  fetchDispensaryOrdersList,
  mapOrdersToRecentDashboardOrders,
  markDispensaryOrderReady,
  type DashboardRecentOrder,
  type MarkDispensaryOrderReadyOk,
} from "../lib/dispensary-dashboard-api";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";
import { PosDeliveriesList } from "./PosDeliveriesList";
import { DashboardEmptyState } from "./DashboardEmptyState";

function OrderStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    on_the_way: "bg-[#111111] text-white",
    pending: "bg-[#F0F0F0] text-[#555555]",
    delivered: "bg-[#F0F0F0] text-[#555555]",
    confirmed: "bg-[#EBF5EB] text-[#2D6B2D]",
    failed: "bg-[#FEE2E2] text-[#991B1B]",
  };
  const labels: Record<string, string> = {
    on_the_way: "On The Way",
    pending: "Pending",
    delivered: "Delivered",
    confirmed: "Confirmed",
    failed: "Failed",
  };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
        variants[status] ?? variants.pending
      }`}
    >
      {labels[status] ?? status}
    </span>
  );
}

function shortOrderId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function recentOrderMatchesSearch(o: DashboardRecentOrder, q: string): boolean {
  if (!q) return true;
  const hay = [
    o.customerFullName,
    o.orderId,
    o.deliveryCity,
    o.deliveryZip,
    o.driver.name,
    o.status,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function RecentOrdersCard({ headerSearch = "" }: { headerSearch?: string }) {
  const { getToken, dispensaryId, orgId, allLocations } = useDispensaryWorkspace();
  // Recent-orders row actions (confirm / mark-ready) target a single store, so
  // in "All locations" mode this card points to the Orders board instead of
  // aggregating. Tiles + active-runs + transporter panels still aggregate.
  const aggregate = allLocations && orgId != null;
  const [tab, setTab] = useState<"drop" | "pos">("drop");
  const queryClient = useQueryClient();
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [markReadyError, setMarkReadyError] = useState<string | null>(null);
  const [markReadyResult, setMarkReadyResult] = useState<MarkDispensaryOrderReadyOk | null>(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["dispensary", "orders", "recent", dispensaryId],
    queryFn: async () => {
      const list = await fetchDispensaryOrdersList(getToken, dispensaryId!, { limit: 80 });
      return mapOrdersToRecentDashboardOrders(list, 80);
    },
    enabled: Boolean(dispensaryId),
    refetchInterval: 30_000,
  });

  const searchNeedle = headerSearch.trim().toLowerCase();

  const awaitingConfirmation = useMemo(() => {
    const pending = orders.filter((o) => o.apiStatus === "pending");
    return searchNeedle ? pending.filter((o) => recentOrderMatchesSearch(o, searchNeedle)) : pending;
  }, [orders, searchNeedle]);

  /**
   * Confirmed but not yet ready for pickup → "Mark ready" candidates. ADR-115
   * splits the legacy `confirmed` status into two `status_v2` values: `prepping`
   * (post-confirm, packing) and `ready` (driver offer fired). Only `prepping`
   * shows the action; `ready` waits for the offer-accept lifecycle to advance.
   */
  const awaitingMarkReady = useMemo(() => {
    const filtered = orders.filter(
      (o) => o.apiStatus === "confirmed" && o.statusV2 === "prepping",
    );
    return searchNeedle ? filtered.filter((o) => recentOrderMatchesSearch(o, searchNeedle)) : filtered;
  }, [orders, searchNeedle]);

  const recentOrders = useMemo(() => {
    // Drop the two action buckets from the recent table so we don't show the
    // same order twice. Anything still in `prepping` shows in the action card,
    // not the recent list.
    const rest = orders.filter(
      (o) =>
        o.apiStatus !== "pending" &&
        !(o.apiStatus === "confirmed" && o.statusV2 === "prepping"),
    );
    const list = searchNeedle ? rest.filter((o) => recentOrderMatchesSearch(o, searchNeedle)) : rest;
    return list.slice(0, 10);
  }, [orders, searchNeedle]);

  /**
   * **Why both an optimistic cache update AND `Promise.all([invalidations])`.**
   *
   * The earlier "Promise.all from onSuccess" pattern relied on React Query
   * flipping `mutation.isPending` only after the refetch resolved — supposed
   * to keep the button in its "Sending…" state until the new server state
   * landed. In practice the refetch was racing AppState/socket events and
   * the action-card row visibly stayed for a beat after the POST returned;
   * staff reported "I click Mark Ready and nothing visibly changes until I
   * refresh the page."
   *
   * Patching this two ways:
   *
   *   1. **Optimistic `setQueryData`** flips the just-mutated order's
   *      `apiStatus` / `statusV2` in the cached list synchronously, so the
   *      `awaitingConfirmation` / `awaitingMarkReady` filters drop the row
   *      from the action card on the next render — no refetch round-trip
   *      needed. The visible "next state" lands the moment the POST
   *      resolves.
   *   2. **`Promise.all([...invalidates])`** still runs in the background
   *      to reconcile against authoritative server state (totals, dispatch
   *      offer surface, driver-assignment side effects). If the optimistic
   *      guess was wrong (e.g. server rolled back), the invalidate-driven
   *      refetch corrects it within milliseconds.
   *
   * Use the helper `optimisticUpdateOrder` to keep the two mutations
   * symmetric — same shape, just a different status patch.
   */

  /**
   * In-place patch on the cached recent-orders list. Returns a new array
   * with the targeted order's fields replaced; leaves the other rows
   * untouched. No-op when the list isn't cached yet (the initial fetch
   * hasn't landed) or when the targeted id isn't present.
   */
  function optimisticUpdateOrder(
    orderId: string,
    patch: Partial<Pick<DashboardRecentOrder, "apiStatus" | "statusV2" | "status">>,
  ): void {
    queryClient.setQueryData<DashboardRecentOrder[]>(
      ["dispensary", "orders", "recent", dispensaryId],
      (prev) =>
        prev?.map((o) => (o.orderId === orderId ? { ...o, ...patch } : o)),
    );
  }

  const confirmMutation = useMutation({
    mutationFn: async (orderId: string) => {
      if (!dispensaryId) throw new Error("no_dispensary");
      const r = await confirmDispensaryOrder(getToken, dispensaryId, orderId);
      if (!r.ok) throw new Error(r.error);
      return r;
    },
    onSuccess: (_r, orderId) => {
      setConfirmError(null);
      // Pending → Confirmed (and status_v2 → 'prepping' so the row
      // moves from `awaitingConfirmation` to `awaitingMarkReady`
      // instantly, surfacing the next action without a refetch).
      optimisticUpdateOrder(orderId, {
        apiStatus: "confirmed",
        status: "confirmed",
        statusV2: "prepping",
      });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dispensary", "orders", "recent", dispensaryId] }),
        queryClient.invalidateQueries({ queryKey: ["dispensary", "metrics", dispensaryId] }),
        queryClient.invalidateQueries({ queryKey: ["dispensary", "active-runs", dispensaryId] }),
      ]);
    },
    onError: (e: Error) => {
      setConfirmError(e.message);
    },
  });

  /**
   * ADR-115 — fires `emitDispatchOfferForOrder` server-side, which creates the
   * 3-min driver offer. Surfaces the result via `markReadyResult` so the
   * dispensary sees whether a driver actually got the offer (or why not,
   * e.g. `no_eligible_driver`). Optimistic update flips the row out of
   * the `awaitingMarkReady` action card immediately so staff don't see
   * a "stuck" Mark Ready button.
   */
  const markReadyMutation = useMutation({
    mutationFn: async (orderId: string) => {
      if (!dispensaryId) throw new Error("no_dispensary");
      const r = await markDispensaryOrderReady(getToken, dispensaryId, orderId);
      if (!r.ok) throw new Error(r.error);
      return r;
    },
    onSuccess: (r, orderId) => {
      setMarkReadyError(null);
      setMarkReadyResult(r);
      // status_v2 → 'ready' drops the row from `awaitingMarkReady`
      // (filter requires statusV2='prepping'). The legacy `apiStatus`
      // stays 'confirmed' — only the v2 enum flips on mark-ready.
      optimisticUpdateOrder(orderId, { statusV2: "ready" });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dispensary", "orders", "recent", dispensaryId] }),
        queryClient.invalidateQueries({ queryKey: ["dispensary", "metrics", dispensaryId] }),
        queryClient.invalidateQueries({ queryKey: ["dispensary", "active-runs", dispensaryId] }),
      ]);
    },
    onError: (e: Error) => {
      setMarkReadyError(e.message);
      setMarkReadyResult(null);
    },
  });

  const avatar = (name: string) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=F0F0F0&color=333333&size=128`;

  const handleConfirmOrder = (order: DashboardRecentOrder) => {
    setConfirmError(null);
    const ok = window.confirm(
      `Confirm order ${shortOrderId(order.orderId)} for $${(order.totalCents / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}${order.prepaidViaPartner ? " (prepaid via partner, nothing due at the door)" : ""}?\n\nThis accepts the order and packs it for pickup.`,
    );
    if (!ok) return;
    confirmMutation.mutate(order.orderId);
  };

  const handleMarkReady = (order: DashboardRecentOrder) => {
    setMarkReadyError(null);
    setMarkReadyResult(null);
    const ok = window.confirm(
      `Mark order ${shortOrderId(order.orderId)} ready for pickup?\n\n` +
        "This sends a 3-minute pickup offer to the next available driver. " +
        "If no driver accepts within the window, the offer expires and you can retry.",
    );
    if (!ok) return;
    markReadyMutation.mutate(order.orderId);
  };

  if (aggregate) {
    return (
      <div className="overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <div className="border-b border-[#F0F0F0] px-5 py-4">
          <h3 className="text-base font-bold text-[#111111]">Recent Orders</h3>
        </div>
        <div className="px-5 py-8 text-center text-sm text-[#777777]">
          <p>Recent-order actions (confirm, mark ready) are per-store.</p>
          <p className="mt-1">
            Open <span className="font-medium text-[#111111]">Orders</span> for the cross-location list, or pick a single store above.
          </p>
        </div>
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between border-b border-[#F0F0F0] px-5 py-4">
      <h3 className="text-base font-bold text-[#111111]">Recent Orders</h3>
      <div className="flex gap-1 rounded-lg bg-[#F4F4F4] p-0.5">
        {(
          [
            ["drop", "Drop"],
            ["pos", "POS deliveries"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              tab === key ? "bg-white text-[#111111] shadow-sm" : "text-[#777777] hover:text-[#111111]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (tab === "pos") {
    return (
      <div className="overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        {header}
        <PosDeliveriesList />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]">
      {header}

      {confirmError ? (
        <div className="border-b border-[#FEE2E2] bg-[#FEF2F2] px-5 py-2 text-sm text-[#991B1B]">{confirmError}</div>
      ) : null}

      {markReadyError ? (
        <div className="border-b border-[#FEE2E2] bg-[#FEF2F2] px-5 py-2 text-sm text-[#991B1B]">
          Mark ready failed: {markReadyError}
        </div>
      ) : null}

      {markReadyResult ? <MarkReadyResultBanner result={markReadyResult} onDismiss={() => setMarkReadyResult(null)} /> : null}

      {isLoading ? (
        <div className="divide-y divide-[#F5F5F3]">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex animate-pulse items-center gap-3 px-5 py-4">
              <div className="h-7 w-7 rounded-full bg-[#F0F0F0]" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-40 rounded bg-[#F0F0F0]" />
                <div className="h-3 w-24 rounded bg-[#F0F0F0]" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {awaitingConfirmation.length > 0 ? (
            <div className="border-b border-[#F0F0F0] bg-[#FAFAF8] px-5 py-4">
              <h4 className="text-sm font-semibold text-[#111111]">Awaiting confirmation</h4>
              <p className="mt-1 text-xs text-[#777777]">
                Tap an order to confirm it and start packing for pickup.
              </p>
              <ul className="mt-3 space-y-2">
                {awaitingConfirmation.map((order) => {
                  const busy = confirmMutation.isPending && confirmMutation.variables === order.orderId;
                  return (
                    <li key={order.orderId}>
                      <button
                        type="button"
                        disabled={confirmMutation.isPending}
                        onClick={() => handleConfirmOrder(order)}
                        className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#E0E0DE] bg-white px-4 py-3 text-left transition-colors hover:border-[#111111] hover:bg-[#F5F5F3] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <img
                            src={avatar(order.customerFullName)}
                            alt=""
                            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-[#111111]">{order.customerFullName}</div>
                            <div className="mt-0.5 text-xs text-[#888888]">
                              #{shortOrderId(order.orderId)} · {order.deliveryCity}, {order.deliveryZip}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 flex-col items-end gap-1">
                          <span className="text-sm font-semibold text-[#111111]">
                            {order.prepaidViaPartner
                              ? "Prepaid via partner"
                              : `$${(order.totalCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                          </span>
                          <span className="rounded-lg bg-[#111111] px-3 py-1 text-2xs font-semibold text-white">
                            {busy ? "Confirming…" : "Tap to confirm"}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {awaitingMarkReady.length > 0 ? (
            <div className="border-b border-[#F0F0F0] bg-[#F5FAF5] px-5 py-4">
              <h4 className="text-sm font-semibold text-[#111111]">Ready for pickup</h4>
              <p className="mt-1 text-xs text-[#777777]">
                Packed orders. Tap to send a 3-minute pickup offer to the next available driver.
              </p>
              <ul className="mt-3 space-y-2">
                {awaitingMarkReady.map((order) => {
                  const busy = markReadyMutation.isPending && markReadyMutation.variables === order.orderId;
                  return (
                    <li key={order.orderId}>
                      <button
                        type="button"
                        disabled={markReadyMutation.isPending}
                        onClick={() => handleMarkReady(order)}
                        className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#C8E6C8] bg-white px-4 py-3 text-left transition-colors hover:border-[#2D6B2D] hover:bg-[#EBF5EB] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <img
                            src={avatar(order.customerFullName)}
                            alt=""
                            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-[#111111]">{order.customerFullName}</div>
                            <div className="mt-0.5 text-xs text-[#888888]">
                              #{shortOrderId(order.orderId)} · {order.deliveryCity}, {order.deliveryZip}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 flex-col items-end gap-1">
                          <span className="text-sm font-semibold text-[#111111]">
                            {order.prepaidViaPartner
                              ? "Prepaid via partner"
                              : `$${(order.totalCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                          </span>
                          <span className="rounded-lg bg-[#2D6B2D] px-3 py-1 text-2xs font-semibold text-white">
                            {busy ? "Sending offer…" : "Mark ready"}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-[#F0F0F0]">
                  {["Customer / transporter", "Order ID", "ETA", "Value", "Status"].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-5 py-3.5 text-left text-2xs font-medium uppercase tracking-wide text-[#AAAAAA]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 && awaitingConfirmation.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12">
                      <DashboardEmptyState
                        icon={<TruckIcon className="mx-auto h-8 w-8 text-[#DDDDDD]" />}
                        message="No recent orders"
                      />
                    </td>
                  </tr>
                ) : recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-sm text-[#888888]">
                      No other recent orders yet. Pending orders appear above.
                    </td>
                  </tr>
                ) : (
                  recentOrders.map((order: DashboardRecentOrder, i: number) => (
                    <tr
                      key={order.orderId}
                      className={`transition-colors hover:bg-[#FAFAFA] ${
                        i < recentOrders.length - 1 ? "border-b border-[#F5F5F3]" : ""
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={order.driver.avatarUrl?.trim() || avatar(order.customerFullName)}
                            alt=""
                            className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
                          />
                          <span className="text-sm text-[#333333]">{order.customerFullName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm font-bold text-[#111111]">#{shortOrderId(order.orderId)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-[#555555]">
                          {order.etaMinutes != null && order.etaMinutes > 0 ? `${order.etaMinutes} mins` : "—"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm font-semibold text-[#111111]">
                          {order.prepaidViaPartner
                            ? "Prepaid via partner"
                            : `$${(order.totalCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <OrderStatusBadge status={order.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Renders the outcome of `POST /mark-ready` so the dispensary sees whether the
 * dispatch offer fired and which driver got it. Three flavors:
 *  - **Offer fired** → green banner with offer id + expiry countdown
 *  - **Already ready** (idempotent re-call) → neutral note
 *  - **Degraded** (no driver, redis missing, etc.) → amber with the reason copy
 */
function MarkReadyResultBanner({
  result,
  onDismiss,
}: {
  result: MarkDispensaryOrderReadyOk;
  onDismiss: () => void;
}) {
  const baseCls = "border-b px-5 py-2 text-sm flex items-start justify-between gap-3";
  const dismissBtn = (
    <button
      type="button"
      onClick={onDismiss}
      className="flex-shrink-0 text-xs text-current opacity-60 hover:opacity-100"
    >
      Dismiss
    </button>
  );

  if (result.offer) {
    const expiresIn = Math.max(0, Math.round((new Date(result.offer.expiresAt).getTime() - Date.now()) / 1000));
    return (
      <div className={`${baseCls} border-[#C8E6C8] bg-[#EBF5EB] text-[#2D6B2D]`}>
        <div>
          <div className="font-semibold">Pickup offer sent.</div>
          <div className="mt-0.5 text-xs">
            Driver has {expiresIn}s to accept. Offer id <span className="font-mono">{result.offer.offerId.slice(0, 8)}…</span>.
          </div>
        </div>
        {dismissBtn}
      </div>
    );
  }

  if (result.alreadyReady || result.reason === "already_ready") {
    return (
      <div className={`${baseCls} border-[#E0E0DE] bg-[#F5F5F3] text-[#555555]`}>
        <div>Order is already ready for pickup. The earlier offer is still on its expiry window.</div>
        {dismissBtn}
      </div>
    );
  }

  const reasonCopy: Record<string, string> = {
    redis_unavailable:
      "Order is ready, but the offer producer is offline (Redis). The retry worker will fire the offer when it comes back up.",
    no_round_robin_state:
      "Order is ready, but no transporter rotation is configured for this dispensary. Add a `dispatch_round_robin` row.",
    no_transporter_available:
      "Order is ready, but no transporter is currently available for this dispensary.",
    no_eligible_driver:
      "Order is ready, but no driver is currently available on the rotation. They'll get the offer once one comes online.",
    invalid_order_status: "Order state changed mid-flight. Refresh and retry.",
    order_not_found: "Order disappeared mid-flight. Refresh.",
  };
  const copy = (result.reason && reasonCopy[result.reason]) || "Order is ready, but no offer was sent.";

  return (
    <div className={`${baseCls} border-[#FCE4A6] bg-[#FFF8E6] text-[#7A4F00]`}>
      <div>
        <div className="font-semibold">Order marked ready.</div>
        <div className="mt-0.5 text-xs">{copy}</div>
      </div>
      {dismissBtn}
    </div>
  );
}
